import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";
import type {
  OpenPositionAggregateSearchBody,
  OpenPositionAggregateSearchParameters,
} from "./use-open-position-customer-aggregate-search.query";
import { OPEN_POSITION_RESOURCE } from "./use-open-position-search.query";

export const OPEN_POSITION_SEARCH_BY_BRANCH_OPERATION = "search/by-branch" as const;
export const OPEN_POSITION_SEARCH_BY_BRANCH_API_PATH =
  `/v1/${OPEN_POSITION_RESOURCE}/${OPEN_POSITION_SEARCH_BY_BRANCH_OPERATION}` as const;

export type OpenPositionBranchAggregateSearchResponse =
  paths[typeof OPEN_POSITION_SEARCH_BY_BRANCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];
export type OpenPositionBranchAggregate = OpenPositionBranchAggregateSearchResponse["data"][number];

export function useOpenPositionBranchAggregateSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<OpenPositionAggregateSearchParameters, OpenPositionAggregateSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    OpenPositionBranchAggregateSearchResponse,
    Error,
    OpenPositionBranchAggregateSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<
      PaginatedOptions<OpenPositionAggregateSearchParameters, OpenPositionAggregateSearchBody>
    >(options);
  const params = handlePaginatedParameters(optionsWithDefaults.parameters);
  const body = optionsWithDefaults.body;

  return useQuery({
    queryKey: [OPEN_POSITION_RESOURCE, OPEN_POSITION_SEARCH_BY_BRANCH_OPERATION, { params, body }],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<OpenPositionBranchAggregateSearchResponse>(
        OPEN_POSITION_SEARCH_BY_BRANCH_API_PATH,
        body,
        { params },
      );

      return response.data;
    },
    staleTime: 15_000,
    ...queryOptions,
    enabled,
  });
}
